/* @flow */
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import idx from 'idx';
import update from 'immutability-helper';

import ToastActions from 'actions/ToastActions';
import PeopleListTable from './PeopleListTable';
import ErrorReporter from 'app/components/ErrorReporter';
import Notice from 'components/Notice';
import Text from 'components/Text';
import GET_ORG_MEMBERS_QUERY from './queries/OrganizationMembersQuery.gql';
import GET_GROUP_MEMBERS_QUERY from './queries/GroupMembersQuery.gql';
import { ALL_ROLES, EVERYONE } from 'utils/roleHelpers';
import { findMatchingRecord, findMatchingRecordIndex } from 'utils/arrayHelpers';

import type {
  GetOrganizationMembers,
  GetOrganizationMembers_organizations_members_members,
} from './queries/__generated__/GetOrganizationMembers';

import type { GetGroupMembers } from './queries/__generated__/GetGroupMembers';

import type { SortDirection } from 'components/ComposableTable';
import type { EntityType, UserRole, GroupInfo } from './PeopleList';
import type { ApolloQueryResult } from 'apollo-client';
import type { errorFragment } from 'queries/__generated__/errorFragment';

export type PeopleListRow = GetOrganizationMembers_organizations_members_members;

type Props = {
  entityType: EntityType,
  entityUuid: string,
  groupInfo: GroupInfo,
  grade: ?string,
  query: ?string,
  role: UserRole,
  rostered: ?string,
  hasNextPage: boolean,
  fetchMore: () => Promise<any>,
  refetch: () => Promise<any>,
  members: PeopleListRow[],
  loading: boolean,
  error: errorFragment,
  onPersonClicked: Object => void,
  onUserAddedToSelection: PeopleListRow => void,
  onUserRemovedFromSelection: PeopleListRow => void,
  onUserChanged: () => void,
  reloadQuery: boolean,
  batchSelectedUserIds: number[],
  openUserId: ?number,
  hasRosterSource: boolean,
  sortColumn: ?string,
  sortDirection: ?SortDirection,
  onSort: (sortColumn: string, sortDirection: ?SortDirection) => void,
};

class PeopleListTableDataContainer extends React.Component<Props> {
  static defaultProps = {
    hasRosterSource: false,
  };

  componentDidUpdate(prevProps: Props) {
    const { reloadQuery, refetch } = this.props;

    if (reloadQuery && !prevProps.reloadQuery) {
      refetch();
    }
  }

  userRole = () => {
    const { query, role } = this.props;

    if (query && query.length > 0) {
      return 'everyone';
    }

    return role;
  };

  renderError = () => {
    ToastActions.showDefaultErrorToast();

    return (
      <Text align="center">
        <ErrorReporter error="PeopleListTableDataContainer - couldn't load data" />
        <Notice color="white">
          Sorry—no data is available at this time. Try refreshing the page.
        </Notice>
      </Text>
    );
  };

  render() {
    const {
      loading,
      error,
      hasNextPage,
      fetchMore,
      entityUuid,
      entityType,
      groupInfo,
      members,
      onPersonClicked,
      onUserAddedToSelection,
      onUserRemovedFromSelection,
      onUserChanged,
      batchSelectedUserIds,
      openUserId,
      hasRosterSource,
      onSort,
    } = this.props;

    if (error) {
      return this.renderError();
    }

    return (
      <PeopleListTable
        entityUuid={entityUuid}
        entityType={entityType}
        hasNextPage={hasNextPage}
        loading={loading}
        fetchMore={fetchMore}
        members={members}
        groupInfo={groupInfo}
        role={this.userRole()}
        batchSelectedUserIds={batchSelectedUserIds}
        openUserId={openUserId}
        onPersonClicked={onPersonClicked}
        onUserAddedToSelection={onUserAddedToSelection}
        onUserRemovedFromSelection={onUserRemovedFromSelection}
        onUserChanged={onUserChanged}
        onSort={onSort}
        hasRosterSource={hasRosterSource}
      />
    );
  }
}

const generateProps = (
  data: ApolloQueryResult<GetOrganizationMembers> | ApolloQueryResult<GetGroupMembers>,
  entityUuid: string,
  contentsKey: 'organizations' | 'classes'
) => {
  return {
    fetchMore: () =>
      data.fetchMore({
        variables: {
          after: idx(
            findMatchingRecord(data[contentsKey], entityUuid),
            _ => _.members.pageInfo.endCursor
          ),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          const fetchMoreContents = findMatchingRecord(fetchMoreResult[contentsKey], entityUuid);
          const prevContents = findMatchingRecord(prev[contentsKey], entityUuid);
          const index = findMatchingRecordIndex(prev[contentsKey], entityUuid);

          if (!fetchMoreContents || !prevContents || index === undefined || index === null) {
            return prev;
          }

          return update(prev, {
            [contentsKey]: {
              [index]: {
                members: {
                  members: {
                    $set: [...prevContents.members.members, ...fetchMoreContents.members.members],
                  },
                  pageInfo: { $set: fetchMoreContents.members.pageInfo },
                },
              },
            },
          });
        },
      }),
    hasNextPage: idx(
      findMatchingRecord(data[contentsKey], entityUuid),
      _ => _.members.pageInfo.hasNextPage
    ),
    members: idx(findMatchingRecord(data[contentsKey], entityUuid), _ => _.members.members),
    loading: data.loading,
    error: data.error,
    refetch: data.refetch,
  };
};

const withOrganizationMembers = graphql(GET_ORG_MEMBERS_QUERY, {
  options: ({ entityUuid, grade, query, role, rostered, sortColumn, sortDirection }) => {
    const isSearching = !!(query && query.length > 0);
    const roles = isSearching || role === EVERYONE ? ALL_ROLES : [role];
    return {
      variables: {
        orgUuid: entityUuid,
        first: 20,
        after: null,
        query,
        roles,
        grade: isSearching ? null : grade,
        rostered: isSearching ? 'all' : rostered,
        attributeName: sortColumn || 'id',
        direction: sortDirection || 'asc',
      },
      fetchPolicy: 'network-only',
    };
  },
  skip: ({ entityType }) => entityType !== 'organization',
  props: ({ data, ownProps: { entityUuid } }) => ({
    ...generateProps(data, entityUuid, 'organizations'),
    hasRosterSource:
      idx(findMatchingRecord(data.organizations, entityUuid), _ => _.sisSyncOverview.syncStatus) !==
      'not_set_up',
  }),
});

const withGroupMembers = graphql(GET_GROUP_MEMBERS_QUERY, {
  options: ({ entityUuid, query, role, sortColumn, sortDirection }) => {
    const isSearching = !!(query && query.length > 0);
    const roles = isSearching || role === EVERYONE ? ALL_ROLES : [role];
    return {
      variables: {
        groupUuid: entityUuid,
        first: 20,
        after: null,
        query,
        roles,
        attributeName: sortColumn || 'first_name',
        direction: sortDirection || 'asc',
      },
      fetchPolicy: 'network-only',
    };
  },
  skip: ({ entityType }) => entityType !== 'group',
  props: ({ data, ownProps: { entityUuid } }) => {
    // TODO change the get classes query to return these things
    // Current classes query doesn't give us everything, so for now modify the results
    const modifiedData = {
      ...data,
      classes: [
        {
          uuid: idx(findMatchingRecord(data.classes, entityUuid), _ => _.uuid),
          members: {
            members: (
              idx(findMatchingRecord(data.classes, entityUuid), _ => _.members.members) || []
            ).map(member =>
              update(member, {
                roles: {
                  $set: idx(member.org_affiliation_view, _ => _.roles) || [member.user.role],
                },
              })
            ),
            pageInfo: idx(findMatchingRecord(data.classes, entityUuid), _ => _.members.pageInfo),
          },
        },
      ],
    };
    return {
      ...generateProps(modifiedData, entityUuid, 'classes'),
      hasRosterSource: false,
    };
  },
});

export { PeopleListTableDataContainer };

export default compose(
  withOrganizationMembers,
  withGroupMembers
)(PeopleListTableDataContainer);
